import { useMemo, useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Avatar from "@/components/base/Avatar";
import StatCard from "@/components/base/StatCard";
import Pagination from "@/components/base/Pagination";
import { Select } from "@/components/base/Field";
import { EmptyState, ErrorState, LoadingState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import CycleScopeBanner from "@/components/feature/CycleScopeBanner";
import { useAuth } from "@/contexts/AuthContext";
import { useAsync } from "@/hooks/useAsync";
import { usePager } from "@/hooks/usePager";
import { useUrlSearch } from "@/hooks/useUrlSearch";
import { getClasses } from "@/services/academicService";
import { getActiveSchoolYear, getSchoolInfo } from "@/services/schoolService";
import {
  getUnpaid,
  recordReminders,
  REMINDER_CHANNEL_LABEL,
  whatsappNumber,
  type UnpaidRow,
} from "@/services/reminderService";
import type { SchoolInfo } from "@/types";
import { formatDate, formatMoney, plural } from "@/utils/format";
import NoticePrintModal from "./components/NoticePrintModal";
import ReminderHistoryModal from "./components/ReminderHistoryModal";

type Mode = "overdue" | "unpaid";

/** Message WhatsApp de relance, prêt à envoyer. */
function whatsappMessage(row: UnpaidRow, school: SchoolInfo, mode: Mode): string {
  const lines = row.lines.map((l) => `• ${l.label} : ${formatMoney(l.remaining)}`).join("\n");
  const total = row.lines.reduce((acc, l) => acc + l.remaining, 0);
  return [
    `Bonjour${row.guardian ? ` ${row.guardian.name}` : ""},`,
    "",
    `${school.name} vous informe que ${mode === "overdue" ? "le paiement suivant est en retard" : "il reste à payer"} pour ${row.firstName} ${row.lastName} (${row.className}) :`,
    lines,
    `Total : ${formatMoney(total)}`,
    "",
    "Merci de régulariser dès que possible auprès de la caisse de l'école. Si le paiement a déjà été effectué, merci de ne pas tenir compte de ce message.",
    "",
    `${school.name}${school.phone ? ` — ${school.phone}` : ""}`,
  ].join("\n");
}

export default function UnpaidPage() {
  const { notify } = useToast();
  const { can, cycles, allowedCycleIds } = useAuth();
  const schoolYear = getActiveSchoolYear();
  const canRemind = can("reminders:create");

  const [mode, setMode] = useState<Mode>("overdue");
  const [cycleFilter, setCycleFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");
  const [search, setSearch] = useUrlSearch();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [printRows, setPrintRows] = useState<{ rows: UnpaidRow[]; label: string } | null>(null);
  const [historyRow, setHistoryRow] = useState<UnpaidRow | null>(null);
  const pager = usePager();

  const { data, loading, error, reload } = useAsync(async () => {
    const [rows, classes, school] = await Promise.all([
      getUnpaid({ schoolYear, mode }),
      getClasses(),
      getSchoolInfo(),
    ]);
    return { rows, classes, school };
  }, [mode, schoolYear]);

  const visibleCycles = cycles.filter((c) => allowedCycleIds.includes(c.id));
  const classes = useMemo(
    () =>
      (data?.classes ?? [])
        .filter((c) => cycleFilter === "all" || c.cycleId === cycleFilter)
        .sort((a, b) => (a.level ?? 99) - (b.level ?? 99) || a.name.localeCompare(b.name)),
    [data, cycleFilter],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.rows ?? []).filter(
      (r) =>
        (cycleFilter === "all" || r.cycleId === cycleFilter) &&
        (classFilter === "all" || r.classId === classFilter) &&
        (!q ||
          `${r.firstName} ${r.lastName}`.toLowerCase().includes(q) ||
          `${r.lastName} ${r.firstName}`.toLowerCase().includes(q) ||
          r.matricule.toLowerCase().includes(q) ||
          (r.guardian?.phone ?? "").replace(/\s/g, "").includes(q.replace(/\s/g, ""))),
    );
  }, [data, cycleFilter, classFilter, search]);

  if (loading && !data) return <LoadingState label="Recherche des impayés..." />;
  if (error || !data) return <ErrorState message={error ?? "Chargement impossible."} onRetry={reload} />;

  const page = pager.slice(filtered, `${mode}|${cycleFilter}|${classFilter}|${search}`);
  const amountOf = (r: UnpaidRow) => (mode === "overdue" ? r.overdueAmount : r.totalRemaining);
  const totalAmount = filtered.reduce((acc, r) => acc + amountOf(r), 0);
  const reminded = filtered.filter((r) => r.reminderCount > 0).length;
  const selectedRows = filtered.filter((r) => selected.has(r.enrollmentId));
  const allOnPageSelected = page.items.length > 0 && page.items.every((r) => selected.has(r.enrollmentId));

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const togglePage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      page.items.forEach((r) => (allOnPageSelected ? next.delete(r.enrollmentId) : next.add(r.enrollmentId)));
      return next;
    });

  const scopeLabel = () => {
    if (selectedRows.length) return `${plural(selectedRows.length, "élève sélectionné", "élèves sélectionnés")}`;
    if (classFilter !== "all") return data.classes.find((c) => c.id === classFilter)?.name ?? "Classe";
    return "Toute l'école";
  };

  const openPrint = (rows: UnpaidRow[], label: string) => {
    if (!rows.length) {
      notify("Aucun élève à qui imprimer un avis.", "error");
      return;
    }
    setPrintRows({ rows, label });
  };

  const sendWhatsapp = async (row: UnpaidRow) => {
    const number = row.guardian ? whatsappNumber(row.guardian.phone) : null;
    if (!number) {
      notify("Numéro du parent absent ou invalide : corrigez-le dans la page Parents d'élèves.", "error");
      return;
    }
    // La fenêtre s'ouvre tout de suite (sinon le navigateur la bloque), la relance est enregistrée ensuite.
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(whatsappMessage(row, data.school, mode))}`, "_blank", "noopener");
    if (!canRemind) return;
    try {
      await recordReminders([row.enrollmentId], "WHATSAPP");
      reload();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Relance non enregistrée.", "error");
    }
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Impayés et relances"
        subtitle={`Familles en retard de paiement — année ${schoolYear}`}
        icon="ri-alarm-warning-line"
        actions={
          <Button
            icon="ri-printer-line"
            onClick={() => openPrint(selectedRows.length ? selectedRows : filtered, scopeLabel())}
            disabled={!filtered.length}
          >
            Imprimer les avis ({selectedRows.length || filtered.length})
          </Button>
        }
      />
      <CycleScopeBanner className="mb-4" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label={mode === "overdue" ? "Élèves en retard" : "Élèves avec un reste à payer"}
          value={String(filtered.length)}
          icon="ri-user-unfollow-line"
          tone="accent"
        />
        <StatCard
          label={mode === "overdue" ? "Montant échu non payé" : "Reste à payer"}
          value={formatMoney(totalAmount)}
          icon="ri-money-dollar-circle-line"
          tone="secondary"
        />
        <StatCard
          label="Déjà relancés"
          value={`${reminded} / ${filtered.length}`}
          icon="ri-notification-badge-line"
          tone="primary"
        />
      </div>

      <Card className="mt-5" padded={false}>
        <div className="flex flex-col gap-3 border-b border-background-200 p-4 lg:flex-row lg:items-end">
          <div className="inline-flex rounded-md border border-background-300 p-0.5">
            {(
              [
                ["overdue", "En retard"],
                ["unpaid", "Tous les restes à payer"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setMode(value);
                  setSelected(new Set());
                }}
                className={`rounded px-3 py-1.5 text-xs font-medium cursor-pointer ${
                  mode === value ? "bg-primary-600 text-white" : "text-foreground-600 hover:bg-background-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {visibleCycles.length > 1 && (
            <Select
              label="Cycle"
              value={cycleFilter}
              onChange={(e) => {
                setCycleFilter(e.target.value);
                setClassFilter("all");
              }}
              wrapperClass="lg:w-44"
            >
              <option value="all">Tous les cycles</option>
              {visibleCycles.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </Select>
          )}
          <Select
            label="Classe"
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            wrapperClass="lg:w-44"
          >
            <option value="all">Toutes les classes</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <div className="flex-1">
            <span className="mb-1.5 block text-xs font-medium text-foreground-700">Recherche</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Élève, matricule ou téléphone du parent"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon="ri-checkbox-circle-line"
              title={mode === "overdue" ? "Aucun retard de paiement" : "Aucun reste à payer"}
              message={
                mode === "overdue"
                  ? "Seuls les frais dont l'échéance (une date dans la fiche de renseignements) est passée sont comptés."
                  : "Toutes les familles sont à jour pour ce filtre."
              }
            />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-sm">
                <thead className="bg-background-100">
                  <tr className="text-left text-xs uppercase tracking-wide text-foreground-500">
                    <th className="w-10 px-4 py-2.5">
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={togglePage}
                        aria-label="Tout sélectionner sur la page"
                        className="h-4 w-4 cursor-pointer"
                      />
                    </th>
                    <th className="px-3 py-2.5 font-medium">Élève</th>
                    <th className="px-3 py-2.5 font-medium">Classe</th>
                    <th className="px-3 py-2.5 font-medium">Parent</th>
                    <th className="px-3 py-2.5 font-medium">Frais dus</th>
                    <th className="px-3 py-2.5 font-medium text-right">{mode === "overdue" ? "Échu" : "Reste"}</th>
                    <th className="px-3 py-2.5 font-medium">Relances</th>
                    <th className="px-3 py-2.5 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {page.items.map((r) => (
                    <tr key={r.enrollmentId} className="border-t border-background-200 align-top hover:bg-background-100/40">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(r.enrollmentId)}
                          onChange={() => toggle(r.enrollmentId)}
                          aria-label={`Sélectionner ${r.firstName} ${r.lastName}`}
                          className="h-4 w-4 cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2.5">
                          <Avatar lastName={r.lastName} firstName={r.firstName} src={r.photo || undefined} size="sm" />
                          <div>
                            <p className="font-medium text-foreground-900">
                              {r.firstName} {r.lastName}
                            </p>
                            <p className="text-xs text-foreground-500">{r.matricule}</p>
                            {r.leftAt && (
                              <Badge tone="accent" icon="ri-logout-box-r-line" className="mt-1">
                                A quitté l'école le {formatDate(r.leftAt)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-foreground-700">{r.className}</td>
                      <td className="px-3 py-3">
                        {r.guardian ? (
                          <>
                            <p className="text-foreground-800">{r.guardian.name}</p>
                            <p className="text-xs text-foreground-500">{r.guardian.phone}</p>
                          </>
                        ) : (
                          <span className="text-xs text-foreground-400">Aucun parent</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <ul className="space-y-0.5 text-xs">
                          {r.lines.map((l) => (
                            <li key={l.label} className={l.overdue ? "text-accent-700" : "text-foreground-600"}>
                              {l.label} : {formatMoney(l.remaining)}
                              {l.deadline && <span className="text-foreground-400"> (avant le {formatDate(l.deadline)})</span>}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-3 py-3 text-right font-semibold text-accent-700">{formatMoney(amountOf(r))}</td>
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          onClick={() => setHistoryRow(r)}
                          className="text-left cursor-pointer"
                          title="Voir l'historique des relances"
                        >
                          {r.lastReminder ? (
                            <>
                              <Badge tone="secondary">
                                {r.reminderCount} relance{r.reminderCount > 1 ? "s" : ""}
                              </Badge>
                              <p className="mt-1 text-xs text-foreground-500">
                                {REMINDER_CHANNEL_LABEL[r.lastReminder.channel]}, {formatDate(r.lastReminder.createdAt)}
                              </p>
                            </>
                          ) : (
                            <Badge tone="danger">Jamais relancé</Badge>
                          )}
                        </button>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            icon="ri-whatsapp-line"
                            onClick={() => void sendWhatsapp(r)}
                            disabled={!r.guardian}
                            title="Ouvrir WhatsApp avec le message de relance"
                          >
                            WhatsApp
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            icon="ri-printer-line"
                            onClick={() => openPrint([r], `${r.firstName} ${r.lastName}`)}
                            title="Imprimer l'avis de cet élève"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {selectedRows.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-background-200 bg-primary-50/60 px-4 py-2.5 text-sm">
                <span className="text-primary-800">{plural(selectedRows.length, "élève")} sélectionné(s)</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                    Tout désélectionner
                  </Button>
                  <Button size="sm" icon="ri-printer-line" onClick={() => openPrint(selectedRows, scopeLabel())}>
                    Imprimer leurs avis
                  </Button>
                </div>
              </div>
            )}
            <Pagination pager={page} className="border-t border-background-200" />
          </>
        )}
      </Card>

      <NoticePrintModal
        rows={printRows?.rows ?? null}
        school={data.school}
        scopeLabel={printRows?.label ?? ""}
        canRecord={canRemind}
        onClose={() => setPrintRows(null)}
        onPrinted={() => {
          setSelected(new Set());
          reload();
        }}
      />
      <ReminderHistoryModal row={historyRow} onClose={() => setHistoryRow(null)} />
    </div>
  );
}
